import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { reportAPI } from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Reports = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const isStaff = user?.role === 'staff';
    const [reportType, setReportType] = useState('daily');
    const [params, setParams] = useState({
        date: new Date().toISOString().split('T')[0],
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
    });
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);

    const generateReport = async () => {
        setLoading(true);
        try {
            let response;
            switch (reportType) {
                case 'daily':
                    response = await reportAPI.daily({ date: params.date });
                    break;
                case 'weekly':
                    response = await reportAPI.weekly({
                        start_date: params.start_date,
                        end_date: params.end_date,
                    });
                    break;
                case 'monthly':
                    response = await reportAPI.monthly({
                        month: params.month,
                        year: params.year,
                    });
                    break;
                case 'yearly':
                    response = await reportAPI.yearly({ year: params.year });
                    break;
                default:
                    response = await reportAPI.daily({ date: params.date });
            }
            setReportData(response.data);
        } catch (error) {
            toast.error(t('report.failedGenerate'));
        } finally {
            setLoading(false);
        }
    };

    const exportPdf = async () => {
        try {
            const response = await reportAPI.exportPdf({ type: reportType, ...params });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error(t('report.failedExportPdf'));
        }
    };

    const exportExcel = async () => {
        try {
            const response = await reportAPI.exportExcel({ type: reportType, ...params });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report_${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.error(t('report.failedExportExcel'));
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-6">{isStaff ? t('report.myTitle') || 'My Reports' : t('report.title')}</h1>

                {/* Report Type Selection */}
                <div className="flex flex-wrap gap-4 mb-6">
                    {['daily', 'weekly', 'monthly', 'yearly'].map((type) => (
                        <button
                            key={type}
                            onClick={() => {
                                setReportType(type);
                                setReportData(null);
                            }}
                            className={`px-4 py-2 rounded-lg ${
                                reportType === type
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {t(`report.${type}`)}
                        </button>
                    ))}
                </div>

                {/* Parameters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {reportType === 'daily' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('report.selectDate')}
                            </label>
                            <input
                                type="date"
                                value={params.date}
                                onChange={(e) => setParams({ ...params, date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                    )}

                    {reportType === 'weekly' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('report.startDate')}
                                </label>
                                <input
                                    type="date"
                                    value={params.start_date}
                                    onChange={(e) => setParams({ ...params, start_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('report.endDate')}
                                </label>
                                <input
                                    type="date"
                                    value={params.end_date}
                                    onChange={(e) => setParams({ ...params, end_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </>
                    )}

                    {reportType === 'monthly' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('report.month')}
                                </label>
                                <select
                                    value={params.month}
                                    onChange={(e) => setParams({ ...params, month: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                        <option key={m} value={m}>
                                            {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('report.year')}
                                </label>
                                <input
                                    type="number"
                                    value={params.year}
                                    onChange={(e) => setParams({ ...params, year: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </>
                    )}

                    {reportType === 'yearly' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('report.year')}
                            </label>
                            <input
                                type="number"
                                value={params.year}
                                onChange={(e) => setParams({ ...params, year: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                        </div>
                    )}
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={generateReport}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? t('report.generating') : t('report.generate')}
                    </button>
                    {reportData && (
                        <>
                            <button
                                onClick={exportPdf}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                {t('report.exportPdf')}
                            </button>
                            <button
                                onClick={exportExcel}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                            >
                                {t('report.exportExcel')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Report Results */}
            {reportData && (
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <div className="text-sm text-blue-600">{t('report.totalCertificates')}</div>
                            <div className="text-3xl font-bold text-blue-800">
                                {reportData.total}
                            </div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                            <div className="text-sm text-green-600">{t('report.active')}</div>
                            <div className="text-3xl font-bold text-green-800">
                                {reportData.active}
                            </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                            <div className="text-sm text-red-600">{t('report.cancelled')}</div>
                            <div className="text-3xl font-bold text-red-800">
                                {reportData.cancelled}
                            </div>
                        </div>
                    </div>

                    {/* Staff Breakdown — only shown for admin users */}
                    {!isStaff && reportData.by_staff && Object.keys(reportData.by_staff).length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold mb-4">{t('report.staffBreakdown')}</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                {t('report.staffName')}
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                {t('report.certificates')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {Object.entries(reportData.by_staff).map(([id, data]) => (
                                            <tr key={id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {data.staff_name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {data.count}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Reports;